import React, { useContext, useEffect, useRef, useState } from "react";
import { CanvasContext } from "../../../contexts/CanvasContext";

export default function VideoComponent({ video }) {
  const videoRef = useRef(null);
  const { hoveredDivision, files } = useContext(CanvasContext);
  const [videoUrl, setVideoUrl] = useState(null);
  const isHovered = video.id === hoveredDivision;
  const [isvideoHovered, setIsvideoHovered] = useState(false);
  const [isvideoFocused, setIsvideoFocused] = useState(false);
  const [isvideoActive, setIsvideoActive] = useState(false);

  useEffect(() => {
    const fetchVideo = async () => {
      if (video.url && video.url.startsWith("./")) {
        const videoName = video.url.replace("./", "");
        const file = files.find((f) => f.name === videoName);
        if (file && file.data) {
          setVideoUrl(file.data);
        } else {
          console.error("Video not found in files:", videoName);
        }
      } else {
        setVideoUrl(video.url);
      }
    };

    fetchVideo();
  }, [video.url, files]);

  const generateKeyframes = () => {
    if (!video.keyframes || video.keyframes.length === 0) return null;

    return video.keyframes.map((kf) => {
      const properties = {};
      let transform = "";

      Object.entries(kf.properties).forEach(([key, value]) => {
        // Remove 'KEY' prefix if present
        let processedKey = key.startsWith("KEY") ? key.slice(3) : key;

        // Handle transform properties
        if (
          processedKey === "scale" ||
          processedKey === "translateX" ||
          processedKey === "translateY" ||
          processedKey === "rotate" ||
          processedKey === "skewX" ||
          processedKey === "skewY"
        ) {
          if (value) {
            switch (processedKey) {
              case "scale":
                transform += `scale(${value}) `;
                break;
              case "translateX":
                transform += `translateX(${value}) `;
                break;
              case "translateY":
                transform += `translateY(${value}) `;
                break;
              case "rotate":
                transform += `rotate(${value}) `;
                break;
              case "skewX":
                transform += `skewX(${value}) `;
                break;
              case "skewY":
                transform += `skewY(${value}) `;
                break;
              default:
                break;
            }
          }
        } else {
          // Convert camelCase to kebab-case for other CSS properties
          properties[processedKey] = value;
        }
      });

      // Add transform property if it was built
      if (transform) {
        properties["transform"] = transform.trim();
      }

      return {
        offset: kf.position / 100,
        ...properties,
      };
    });
  };
  useEffect(() => {
    if (videoRef.current && video.keyframes && video.keyframes.length > 0) {
      const keyframes = generateKeyframes();
      const animation = videoRef.current.animate(keyframes, {
        duration: parseFloat(video.animationDuration) * 1000 || 1000,
        delay: parseFloat(video.animationDelay) * 1000 || 0,
        iterations:
          video.animationIterationCount === "infinite"
            ? Infinity
            : parseFloat(video.animationIterationCount) || 1,
        direction: video.animationDirection || "normal",
        fill: video.animationFillMode || "none",
        easing: video.animationTimingFunction || "linear",
      });

      return () => animation.cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    video.keyframes,
    video.animationDuration,
    video.animationDelay,
    video.animationIterationCount,
    video.animationDirection,
    video.animationFillMode,
    video.animationTimingFunction,
  ]);

  const focusedStyles =
    isvideoFocused && video.pseudoClass === "focus"
      ? {
          width: video.focusedwidth || video.width || "300px",
          height: video.focusedheight || video.height || "200px",

          padding: video.focusedpadding || video.padding || "0",
          paddingTop: video.focusedpaddingTop || video.focusedpadding || "0",
          paddingRight:
            video.focusedpaddingRight || video.focusedpadding || "0",
          paddingBottom:
            video.focusedpaddingBottom || video.focusedpadding || "0",
          paddingLeft: video.focusedpaddingLeft || video.focusedpadding || "0",
          margin: video.focusedmargin || video.margin || "0px",
          marginTop: video.focusedmarginTop || video.focusedmargin || "0px",
          marginRight: video.focusedmarginRight || video.focusedmargin || "0px",
          marginBottom:
            video.focusedmarginBottom || video.focusedmargin || "0px",
          marginLeft: video.focusedmarginLeft || video.focusedmargin || "0px",

          borderTop:
            video.focusedborderTop ||
            video.focusedborder ||
            video.border ||
            "none",
          borderRight:
            video.focusedborderRight ||
            video.focusedborder ||
            video.border ||
            "none",
          borderBottom:
            video.focusedborderBottom ||
            video.focusedborder ||
            video.border ||
            "none",
          borderLeft:
            video.focusedborderLeft ||
            video.focusedborder ||
            video.border ||
            "none",
          borderSapcing:
            video.focusedborderSpacing || video.borderSpacing || "0px",
          borderCollapse:
            video.focusedborderCollapse || video.borderCollapse || "0px",
          borderRadius: video.focusedborderRadius || video.borderRadius || "0",
          outline: video.focusedoutline || video.outline || "none",

          position: video.focusedposition || video.position || "relative",
          top: video.focusedtop || video.top || "auto",
          right: video.focusedright || video.right || "auto",
          bottom: video.focusedbottom || video.bottom || "auto",
          left: video.focusedleft || video.left || "auto",
          zIndex: video.focusedzIndex || video.zIndex || "auto",

          boxShadow: video.focusedboxShadow || video.boxShadow || "none",
          opacity: video.focusedOpacity || video.opacity || 1,
          cursor: video.focusedcursor || video.cursor || "default",
          visibility: video.focusedvisibility || "visible",

          filter: video.focusedfilter || video.filter || "none",
          backdropFilter:
            video.focusedbackdropFilter || video.backdropFilter || "none",
          mixBlendMode:
            video.focusedmixBlendMode || video.mixBlendMode || "normal",
          transform: video.focusedtransform || video.transform || "none",
          transition: video.focusedtransition || video.transition || "none",
        }
      : {};

  const hoverStyles =
    isvideoHovered && video.pseudoClass === "hover"
      ? {
          width: video.hoveredwidth || video.width || "300px",
          height: video.hoveredheight || video.height || "200px",

          padding: video.hoveredpadding || video.padding || "0",
          paddingTop: video.hoveredpaddingTop || video.hoveredpadding || "0",
          paddingRight:
            video.hoveredpaddingRight || video.hoveredpadding || "0",
          paddingBottom:
            video.hoveredpaddingBottom || video.hoveredpadding || "0",
          paddingLeft: video.hoveredpaddingLeft || video.hoveredpadding || "0",
          margin: video.hoveredmargin || video.margin || "0px",
          marginTop: video.hoveredmarginTop || video.hoveredmargin || "0px",
          marginRight: video.hoveredmarginRight || video.hoveredmargin || "0px",
          marginBottom:
            video.hoveredmarginBottom || video.hoveredmargin || "0px",
          marginLeft: video.hoveredmarginLeft || video.hoveredmargin || "0px",

          borderTop:
            video.hoveredborderTop ||
            video.hoveredborder ||
            video.border ||
            "none",
          borderRight:
            video.hoveredborderRight ||
            video.hoveredborder ||
            video.border ||
            "none",
          borderBottom:
            video.hoveredborderBottom ||
            video.hoveredborder ||
            video.border ||
            "none",
          borderLeft:
            video.hoveredborderLeft ||
            video.hoveredborder ||
            video.border ||
            "none",
          borderSapcing:
            video.hoveredborderSpacing || video.borderSpacing || "0px",
          borderCollapse:
            video.hoveredborderCollapse || video.borderCollapse || "0px",
          borderRadius: video.hoveredborderRadius || video.borderRadius || "0",
          outline: video.hoveredoutline || video.outline || "none",

          position: video.hoveredposition || video.position || "relative",
          top: video.hoveredtop || video.top || "auto",
          right: video.hoveredright || video.right || "auto",
          bottom: video.hoveredbottom || video.bottom || "auto",
          left: video.hoveredleft || video.left || "auto",
          zIndex: video.hoveredzIndex || video.zIndex || "auto",

          boxShadow: video.hoveredboxShadow || video.boxShadow || "none",
          opacity: video.hoveredOpacity || video.opacity || 1,
          cursor: video.hoveredcursor || video.cursor || "default",
          visibility: video.hoveredvisibility || "visible",

          filter: video.hoveredfilter || video.filter || "none",
          backdropFilter:
            video.hoveredbackdropFilter || video.backdropFilter || "none",
          mixBlendMode:
            video.hoveredmixBlendMode || video.mixBlendMode || "normal",
          transform: video.hoveredtransform || video.transform || "none",
          transition: video.hoveredtransition || video.transition || "none",
        }
      : {};

  const activeStyles =
    isvideoActive && video.pseudoClass === "active"
      ? {
          width: video.activatedwidth || video.width || "300px",
          height: video.activatedheight || video.height || "200px",

          padding: video.activatedpadding || video.padding || "0",
          paddingTop:
            video.activatedpaddingTop || video.activatedpadding || "0",
          paddingRight:
            video.activatedpaddingRight || video.activatedpadding || "0",
          paddingBottom:
            video.activatedpaddingBottom || video.activatedpadding || "0",
          paddingLeft:
            video.activatedpaddingLeft || video.activatedpadding || "0",
          margin: video.activatedmargin || video.margin || "0px",
          marginTop: video.activatedmarginTop || video.activatedmargin || "0px",
          marginRight:
            video.activatedmarginRight || video.activatedmargin || "0px",
          marginBottom:
            video.activatedmarginBottom || video.activatedmargin || "0px",
          marginLeft:
            video.activatedmarginLeft || video.activatedmargin || "0px",

          borderTop:
            video.activatedborderTop ||
            video.activatedborder ||
            video.border ||
            "none",
          borderRight:
            video.activatedborderRight ||
            video.activatedborder ||
            video.border ||
            "none",
          borderBottom:
            video.activatedborderBottom ||
            video.activatedborder ||
            video.border ||
            "none",
          borderLeft:
            video.activatedborderLeft ||
            video.activatedborder ||
            video.border ||
            "none",
          borderSapcing:
            video.activatedborderSpacing || video.borderSpacing || "0px",
          borderCollapse:
            video.activatedborderCollapse || video.borderCollapse || "0px",
          borderRadius:
            video.activatedborderRadius || video.borderRadius || "0",
          outline: video.activatedoutline || video.outline || "none",

          position: video.activatedposition || video.position || "relative",
          top: video.activatedtop || video.top || "auto",
          right: video.activatedright || video.right || "auto",
          bottom: video.activatedbottom || video.bottom || "auto",
          left: video.activatedleft || video.left || "auto",
          zIndex: video.activatedzIndex || video.zIndex || "auto",

          boxShadow: video.activatedboxShadow || video.boxShadow || "none",
          opacity: video.activatedOpacity || video.opacity || 1,
          cursor: video.activatedcursor || video.cursor || "default",
          visibility: video.activatedvisibility || "visible",

          filter: video.activatedfilter || video.filter || "none",
          backdropFilter:
            video.activatedbackdropFilter || video.backdropFilter || "none",
          mixBlendMode:
            video.activatedmixBlendMode || video.mixBlendMode || "normal",
          transform: video.activatedtransform || video.transform || "none",
          transition: video.activatedtransition || video.transition || "none",
        }
      : {};

  const VideoStyle = {
    width: video.width || "300px",
    height: video.height || "200px",

    paddingRight: video.paddingRight || video.padding || "0px",
    paddingBottom: video.paddingBottom || video.padding || "0px",
    paddingLeft: video.paddingLeft || video.padding || "0px",
    marginTop: video.marginTop || video.margin || "0px",
    marginRight: video.marginRight || video.margin || "0px",
    marginBottom: video.marginBottom || video.margin || "0px",
    marginLeft: video.marginLeft || video.margin || "0px",

    borderTop: isHovered
      ? "1px dashed #555"
      : video.borderTop || video.border || "none",
    borderRight: isHovered
      ? "1px dashed #555"
      : video.borderRight || video.border || "none",
    borderBottom: isHovered
      ? "1px dashed #555"
      : video.borderBottom || video.border || "none",
    borderLeft: isHovered
      ? "1px dashed #555"
      : video.borderLeft || video.border || "none",
    borderSapcing: video.borderSpacing || "0px",
    borderCollapse: video.borderCollapse || "0px",
    borderRadius: video.borderRadius || "0",
    outline: video.outline || "none",

    position: video.position || "static",
    top: video.top || "auto",
    right: video.right || "auto",
    bottom: video.bottom || "auto",
    left: video.left || "auto",
    zIndex: video.zIndex || "auto",

    boxShadow: video.boxShadow || "none",
    opacity: video.opacity || "1",
    boxSizing: video.boxSizing || "border-box",
    display: video.display || "block",
    visibility: video.visibility || "visible",

    filter: video.filter || "none",
    backdropFilter: video.backdropFilter || "none",
    mixBlendMode: video.mixBlendMode || "normal",
    objectFit: video.objectFit || "fill",
    objectPosition: video.objectPosition || "center",
    cursor: video.cursor || "auto",
    transition: video.transition || "none",
    transform: video.transform || "none",
    clipPath: video.clipPath || "none",

    scale: isHovered ? "1.01" : "1",
    ...hoverStyles,
    ...focusedStyles,
    ...activeStyles,
  };

  return (
    <video
      ref={videoRef}
      style={VideoStyle}
      src={videoUrl || "..."}
      poster={video.posterUrl || video.poster || "..."}
      autoPlay={video.autoplay || false}
      controls={video.controls !== false}
      loop={video.loop || false}
      muted={video.muted || false}
      preload={video.preload || "auto"}
      playsInline={video.playsInline || false}
      onMouseEnter={() => setIsvideoHovered(true)}
      onMouseLeave={() => setIsvideoHovered(false)}
      onFocus={() => setIsvideoFocused(true)}
      onBlur={() => setIsvideoFocused(false)}
      onMouseDown={() => setIsvideoActive(true)}
      onMouseUp={() => setIsvideoActive(false)}
    >
      Your browser does not support the video tag.
    </video>
  );
}
